import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  CloudUpload,
  DesignServices,
  OpenInNew,
  Print,
} from '@mui/icons-material';
import {
  Artifact,
  platformApi,
  Project,
  ProjectMembership,
} from '../../services/platformApi';
import { formatTranslatedLabel } from '../../i18n';
import { useLanguage } from '../../LanguageContext';
import { getStatusColor } from '../projectConstants';
import { ArtifactFormState, initialArtifactForm } from './workspaceTypes';
import { artifactTypes, formatFileSize } from './workspaceConfig';

interface ArtifactsTabProps {
  artifactFile: File | null;
  artifactForm: ArtifactFormState;
  artifactUploadError: string | null;
  artifacts: Artifact[];
  onCreateArtifact: () => Promise<boolean>;
  onSendStoryDocumentToDesign: (artifact: Artifact) => void;
  onSendStoryDocumentToPublishing: (artifact: Artifact) => void;
  project: Project | null;
  memberships: ProjectMembership[];
  saving: boolean;
  setArtifactFile: Dispatch<SetStateAction<File | null>>;
  setArtifactForm: Dispatch<SetStateAction<ArtifactFormState>>;
  setArtifactUploadError: Dispatch<SetStateAction<string | null>>;
}

export const ArtifactsTab: React.FC<ArtifactsTabProps> = ({
  artifactFile,
  artifactForm,
  artifactUploadError,
  artifacts,
  onCreateArtifact,
  onSendStoryDocumentToDesign,
  onSendStoryDocumentToPublishing,
  project,
  memberships,
  saving,
  setArtifactFile,
  setArtifactForm,
  setArtifactUploadError,
}) => {
  const { locale, t } = useLanguage();
  const [artifactDialogOpen, setArtifactDialogOpen] = useState(false);
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(null);
  const [sortKey, setSortKey] = useState<ArtifactSortKey>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const creatingUserLabel = (artifact: Artifact): string => {
    if (!artifact.uploader_user_id) {
      return t('notRecorded');
    }
    const membership = memberships.find((item) => item.user_id === artifact.uploader_user_id);
    return membership?.user_display_name || membership?.user_email || `${t('user')} #${artifact.uploader_user_id}`;
  };

  const sortArtifacts = (items: Artifact[]) =>
    [...items].sort((first, second) => {
      const firstValue = artifactSortValue(first, sortKey, creatingUserLabel);
      const secondValue = artifactSortValue(second, sortKey, creatingUserLabel);
      const comparison =
        typeof firstValue === 'number' && typeof secondValue === 'number'
          ? firstValue - secondValue
          : String(firstValue).localeCompare(String(secondValue), locale, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });

  const sortedArtifacts = sortArtifacts(artifacts);

  const requestSort = (key: ArtifactSortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'created_at' ? 'desc' : 'asc');
  };

  const closeArtifactDialog = () => {
    if (saving) {
      return;
    }
    setArtifactDialogOpen(false);
    setArtifactFile(null);
    setArtifactForm(initialArtifactForm);
    setArtifactUploadError(null);
  };

  const handleCreateFromDialog = async () => {
    const created = await onCreateArtifact();
    if (created) {
      setArtifactDialogOpen(false);
    }
  };

  const renderSortLabel = (key: ArtifactSortKey, label: string) => (
    <TableSortLabel
      active={sortKey === key}
      direction={sortKey === key ? sortDirection : 'asc'}
      onClick={() => requestSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  const renderArtifactContent = (artifact: Artifact) => {
    const downloadUrl = project ? platformApi.getProjectArtifactDownloadUrl(project.id, artifact.id) : '';
    if (artifact.artifact_type === 'photo' && downloadUrl) {
      return (
        <Box
          component="img"
          src={downloadUrl}
          alt={artifact.title}
          sx={{
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 520,
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      );
    }
    if (artifact.artifact_type === 'audio' && downloadUrl) {
      return <Box component="audio" src={downloadUrl} controls sx={{ width: '100%' }} />;
    }
    if (artifact.artifact_type === 'video' && downloadUrl) {
      return <Box component="video" src={downloadUrl} controls sx={{ maxHeight: 520, width: '100%' }} />;
    }
    if (artifact.text_content) {
      return (
        <Box
          component="pre"
          sx={{
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            fontFamily: 'inherit',
            maxHeight: 520,
            overflow: 'auto',
            p: 2,
            whiteSpace: 'pre-wrap',
          }}
        >
          {artifact.text_content}
        </Box>
      );
    }
    if (downloadUrl) {
      return (
        <Button href={downloadUrl} target="_blank" rel="noreferrer" startIcon={<OpenInNew />}>
          {t('openArtifactFile')}
        </Button>
      );
    }
    if (artifact.external_url) {
      return (
        <Button href={artifact.external_url} target="_blank" rel="noreferrer" startIcon={<OpenInNew />}>
          {t('openExternalSource')}
        </Button>
      );
    }
    return <Typography color="text.secondary">{t('noPreviewAvailable')}</Typography>;
  };

  const renderArtifactRows = () => (
    <TableBody>
      {sortedArtifacts.map((artifact) => {
        const isStoryDocument = artifact.artifact_type === 'story_document';
        const metadata = artifact.metadata_json || {};
        return (
          <TableRow
            key={artifact.id}
            hover
            onClick={() => setSelectedArtifact(artifact)}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell>
              <Chip size="small" label={formatTranslatedLabel(t, artifact.artifact_type)} variant="outlined" />
            </TableCell>
            <TableCell>{formatTranslatedLabel(t, getArtifactAddedMethod(artifact))}</TableCell>
            <TableCell>{artifact.created_at ? new Date(artifact.created_at).toLocaleString(locale) : t('notRecorded')}</TableCell>
            <TableCell>{creatingUserLabel(artifact)}</TableCell>
            <TableCell sx={{ minWidth: 260 }}>
              <Typography fontWeight={700}>{artifact.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {artifact.description || artifact.file_name || artifact.external_url || t('noDescription')}
              </Typography>
              {isStoryDocument && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={t('modifiable')} variant="outlined" />
                  <Chip size="small" label={t('reviewable')} variant="outlined" />
                  {metadata.design_ready === true && <Chip size="small" label={t('designReady')} color="info" />}
                  {metadata.publishing_ready === true && <Chip size="small" label={t('publishingReady')} color="success" />}
                </Stack>
              )}
            </TableCell>
            <TableCell>
              <Chip size="small" label={formatTranslatedLabel(t, artifact.status)} color={getStatusColor(artifact.status)} />
            </TableCell>
            <TableCell>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  {artifact.file_name || artifact.external_url || (artifact.text_content ? t('text') : t('notRecorded'))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[
                    artifact.file_size ? formatFileSize(artifact.file_size) : '',
                    artifact.content_type || '',
                    artifact.interview_session_id ? `${t('interviewSession')} #${artifact.interview_session_id}` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ') || t('notRecorded')}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell align="right">
              <Stack direction="row" spacing={1} justifyContent="flex-end" onClick={(event) => event.stopPropagation()}>
                {(artifact.storage_key || artifact.external_url) && project && (
                  <Button
                    href={platformApi.getProjectArtifactDownloadUrl(project.id, artifact.id)}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<OpenInNew />}
                  >
                    {t('open')}
                  </Button>
                )}
                {isStoryDocument && (
                  <>
                    <Button
                      onClick={() => onSendStoryDocumentToDesign(artifact)}
                      disabled={saving}
                      startIcon={<DesignServices />}
                    >
                      {t('design')}
                    </Button>
                    <Button
                      onClick={() => onSendStoryDocumentToPublishing(artifact)}
                      disabled={saving}
                      startIcon={<Print />}
                    >
                      {t('publish')}
                    </Button>
                  </>
                )}
              </Stack>
            </TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('artifacts')}</Typography>
        <Tooltip title={t('uploadArtifact')}>
          <span>
            <IconButton
              size="small"
              aria-label={t('uploadArtifact')}
              onClick={() => {
                setArtifactUploadError(null);
                setArtifactDialogOpen(true);
              }}
              disabled={saving}
            >
              <Add fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Dialog open={artifactDialogOpen} onClose={closeArtifactDialog} fullWidth maxWidth="md">
        <DialogTitle>{t('uploadArtifact')}</DialogTitle>
        <DialogContent>
          {artifactUploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {artifactUploadError}
            </Alert>
          )}
          <Grid container spacing={2} alignItems="center" sx={{ pt: 1 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="artifact-dialog-type-label">{t('type')}</InputLabel>
                <Select
                  labelId="artifact-dialog-type-label"
                  label={t('type')}
                  value={artifactForm.artifactType}
                  onChange={(event) => {
                    setArtifactUploadError(null);
                    setArtifactForm({ ...artifactForm, artifactType: event.target.value });
                  }}
                >
                  {artifactTypes.map((artifactType) => (
                    <MenuItem key={artifactType.value} value={artifactType.value}>
                      {formatTranslatedLabel(t, artifactType.value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label={t('title')}
                value={artifactForm.title}
                onChange={(event) => {
                  setArtifactUploadError(null);
                  setArtifactForm({ ...artifactForm, title: event.target.value });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('description')}
                value={artifactForm.description}
                onChange={(event) => {
                  setArtifactUploadError(null);
                  setArtifactForm({ ...artifactForm, description: event.target.value });
                }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                  {t('file')}
                  <input
                    type="file"
                    accept={
                      artifactForm.artifactType === 'story_document'
                        ? '.doc,.docx,.pdf,application/msword,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                        : undefined
                    }
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setArtifactUploadError(null);
                      setArtifactFile(file);
                      if (file && !artifactForm.title) {
                        setArtifactForm({ ...artifactForm, title: file.name });
                      }
                    }}
                  />
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {artifactFile?.name || t('noFileSelected')}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeArtifactDialog} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateFromDialog}
            disabled={saving || !artifactForm.title.trim() || !artifactFile}
          >
            {t('add')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedArtifact)} onClose={() => setSelectedArtifact(null)} fullWidth maxWidth="lg">
        {selectedArtifact && (
          <>
            <DialogTitle>{selectedArtifact.title}</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">{t('type')}</Typography>
                    <Typography>{formatTranslatedLabel(t, selectedArtifact.artifact_type)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">{t('howAdded')}</Typography>
                    <Typography>{formatTranslatedLabel(t, getArtifactAddedMethod(selectedArtifact))}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">{t('creatingUser')}</Typography>
                    <Typography>{creatingUserLabel(selectedArtifact)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">{t('created')}</Typography>
                    <Typography>
                      {selectedArtifact.created_at ? new Date(selectedArtifact.created_at).toLocaleString(locale) : t('notRecorded')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">{t('status')}</Typography>
                    <Typography>{formatTranslatedLabel(t, selectedArtifact.status)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">{t('fileDetails')}</Typography>
                    <Typography>
                      {[
                        selectedArtifact.file_name || selectedArtifact.external_url || '',
                        selectedArtifact.file_size ? formatFileSize(selectedArtifact.file_size) : '',
                        selectedArtifact.content_type || '',
                      ]
                        .filter(Boolean)
                        .join(' · ') || t('notRecorded')}
                    </Typography>
                  </Grid>
                  {selectedArtifact.description && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">{t('description')}</Typography>
                      <Typography>{selectedArtifact.description}</Typography>
                    </Grid>
                  )}
                </Grid>
                {renderArtifactContent(selectedArtifact)}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedArtifact(null)}>{t('close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Stack spacing={2}>
        {artifacts.length === 0 && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography color="text.secondary" align="center" sx={{ py: 5 }}>
              {t('noArtifactsYet')}
            </Typography>
          </Paper>
        )}
        {artifacts.length > 0 && (
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{renderSortLabel('type', t('type'))}</TableCell>
                    <TableCell>{renderSortLabel('added_method', t('howAdded'))}</TableCell>
                    <TableCell>{renderSortLabel('created_at', t('created'))}</TableCell>
                    <TableCell>{renderSortLabel('creating_user', t('creatingUser'))}</TableCell>
                    <TableCell>{renderSortLabel('name', t('nameDescription'))}</TableCell>
                    <TableCell>{renderSortLabel('status', t('status'))}</TableCell>
                    <TableCell>{renderSortLabel('details', t('commonParameters'))}</TableCell>
                    <TableCell align="right">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                {renderArtifactRows()}
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>
    </Stack>
  );
};

type ArtifactSortKey = 'type' | 'added_method' | 'created_at' | 'creating_user' | 'name' | 'status' | 'details';

const getArtifactAddedMethod = (artifact: Artifact): string => {
  const metadata = artifact.metadata_json || {};
  if (typeof metadata.created_by === 'string') {
    return metadata.created_by;
  }
  if (typeof metadata.source === 'string') {
    return metadata.source;
  }
  if (artifact.storage_key || artifact.file_name) {
    return 'uploaded';
  }
  if (artifact.external_url) {
    return 'linked';
  }
  if (artifact.text_content) {
    return 'created';
  }
  return 'created';
};

const artifactSortValue = (
  artifact: Artifact,
  key: ArtifactSortKey,
  creatingUserLabel: (artifact: Artifact) => string
): string | number => {
  switch (key) {
    case 'type':
      return artifact.artifact_type;
    case 'added_method':
      return getArtifactAddedMethod(artifact);
    case 'created_at':
      return artifact.created_at ? new Date(artifact.created_at).getTime() || 0 : 0;
    case 'creating_user':
      return creatingUserLabel(artifact);
    case 'name':
      return `${artifact.title} ${artifact.description || ''}`;
    case 'status':
      return artifact.status;
    case 'details':
      return `${artifact.file_name || artifact.external_url || ''} ${artifact.content_type || ''} ${artifact.file_size || 0}`;
    default:
      return '';
  }
};
